import { CONNECTOR_NAMES, type ConnectorKind } from '@cyberscore/contracts';
import { Plug, RefreshCw } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { ReadingList, type Reading } from './Readings';

interface Connection {
  id: string;
  kind: ConnectorKind;
  name: string;
  status: 'pending' | 'connected' | 'failed' | 'revoked';
  error: string | null;
  last_sync_at: string | null;
  run: { status: 'ok' | 'failed'; error: string | null; created_at: string } | null;
  readings: Reading[];
}

const STATUS: Record<Connection['status'], string> = { pending: 'Waiting for the admin’s consent', connected: 'Connected', failed: 'Not connected', revoked: 'Disconnected' }; // prettier-ignore
const when = (iso: string) => new Date(iso).toLocaleString('en-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); // prettier-ignore

/**
 * Connected systems (task 48): the business links its Microsoft 365 with an admin's consent, read-only;
 * a sync shows what its settings say about the controls, and staff can record those as answers.
 */
export function ConnectionsCard({ id }: { id: string }) {
  const { tenant } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const canConnect = can(tenant, 'connections.create');
  const staff = can(tenant, 'connections.accept');
  const key = ['connections', tenant?.id, id];
  const list = useQuery({ queryKey: key, queryFn: () => api<Connection[]>('GET', `/assessments/${id}/connections`), enabled: !!tenant }); // prettier-ignore
  const done = (rows: Connection[]) => {
    qc.setQueryData(key, rows);
    void qc.invalidateQueries({ queryKey: ['assessments', tenant?.id, id] });
  };
  const fail = (e: Error) => toast(e.message, 'bad');
  const start = useMutation({
    mutationFn: (kind: ConnectorKind) => api<{ consent_url: string }>('POST', `/assessments/${id}/connections`, { kind }),
    onSuccess: (r) => window.location.assign(r.consent_url),
    onError: (e) => (e instanceof ApiError && e.status === 503 ? toast('Microsoft 365 connections are not set up yet.', 'bad') : fail(e)),
  }); // prettier-ignore
  const act = useMutation({
    mutationFn: (a: { cid: string; what: 'sync' | 'revoke' } | { cid: string; what: 'accept'; control: string }) =>
      api<Connection[]>('POST', `/assessments/${id}/connections/${a.cid}/${a.what}`, a.what === 'accept' ? { control: a.control } : undefined), // prettier-ignore
    onSuccess: done,
    onError: fail,
  });

  if (list.isPending || list.isError) return null;
  const live = list.data.find((c) => c.status === 'connected' || c.status === 'pending');
  const last = live ?? list.data[0];

  return (
    <section className="card pad" aria-labelledby="conn-h">
      <div className="row-between">
        <div id="conn-h" className="eyebrow">Connected systems</div>
        {last && <span className={`pill ${last.status === 'connected' ? 'ok' : last.status === 'pending' ? 'warn' : 'neutral'}`}>{STATUS[last.status]}</span>}
      </div>
      {!last || !live ? (
        <>
          <p className="muted small">
            Connect Microsoft 365 and we read your sign-in settings (MFA, legacy sign-in) instead of asking. Read-only; an admin of your Microsoft 365 approves it.
          </p>
          {last?.status === 'failed' && last.error && <div className="notice small">{last.error}</div>}
          {canConnect && (
            <button type="button" className="btn btn-sm" disabled={start.isPending} onClick={() => start.mutate('microsoft365')}>
              <Plug size={14} /> Connect {CONNECTOR_NAMES.microsoft365}
            </button>
          )}
        </>
      ) : live.status === 'pending' ? (
        <p className="muted small">Waiting for a Microsoft 365 admin to approve. The link lasts an hour; connect again for a new one.</p>
      ) : (
        <>
          <p className="muted small">
            {live.name} · {live.last_sync_at ? `read ${when(live.last_sync_at)}` : 'not read yet'}
          </p>
          {live.run?.status === 'failed' && <div className="notice small">The last read failed: {live.run.error}</div>}
          {live.readings.length > 0 && <ReadingList readings={live.readings} onAccept={staff ? (control) => act.mutate({ cid: live.id, what: 'accept', control }) : undefined} busy={act.isPending} />}
          {canConnect && (
            <div className="row gap mt">
              <button type="button" className="btn btn-sm" disabled={act.isPending} onClick={() => act.mutate({ cid: live.id, what: 'sync' })}>
                <RefreshCw size={14} /> {act.isPending ? 'Reading…' : 'Read now'}
              </button>
              <button type="button" className="btn btn-sm btn-ghost" disabled={act.isPending} onClick={() => act.mutate({ cid: live.id, what: 'revoke' })} title="To withdraw consent in Microsoft too, remove the app in the Microsoft Entra admin centre">
                Disconnect
              </button>
            </div>
          )}
        </>
      )}
    </section>
  ); // prettier-ignore
}
